import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage, resolveCopy } from 'src/utils/resolveApiError';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { eAlertType } from '../../../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../../../providers/ToastProvider/Toast.hook.ts';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context.ts';
import { useWorkspaceContext } from '../../../../../../providers/WorkspaceProvider/Workspace.context.ts';
import useChatActions from '../../../../Chat.actions.ts';
import { ChatService } from '../../../../Chat.service.ts';
import { tAdvanceSearchLead } from '../../../../../AdvanceSearch/AdvanceSearchModal.types.ts';
import { tAttributesForm } from './ChatInfoAttributes.types.ts';
import { getTagName } from './ChatInfoAttributes.tag.utils.ts';
import { invalidateWorkspaceTags } from './ChatInfoAttributes.tag.catalog.ts';
import { useBoardSlice } from 'src/pages/Pipe/PipeBoard.context';

export default function useChatInfo(
  displayedLead: tAdvanceSearchLead | null = null,
) {
  const { currentWorkspaceId } = useWorkspaceContext();
  const { currentUserId } = useUserContext();
  const { updateLead } = useChatActions();
  const { onLeadTagsChanged } = useBoardSlice();
  const {
    selectedLeadForChatModal,
    setSelectedLeadForChatModal,
    tagOpsInFlight,
  } = useModalsContext();

  const { addAlert } = useToastContext();
  // Bound as `translate` on purpose: the tag callbacks below already use `t` as a
  // parameter name, and shadowing it there would be easy to misread.
  const { t: translate } = useTranslation('errors/errors');
  const { t: translateChat } = useTranslation('chat/chat');

  async function onChatInfoAttributesSubmit(
    values: tAttributesForm,
  ): Promise<boolean> {
    const lead = displayedLead ?? selectedLeadForChatModal;
    if (!lead?.id || !currentWorkspaceId || !currentUserId) return false;

    const newValue: Partial<tAttributesForm> = {};
    if (values.full_name !== '') newValue.full_name = values.full_name;
    if (values.email !== '') newValue.email = values.email;
    if (values.company !== '') newValue.company = values.company;
    newValue.revenue = values.revenue;
    newValue.value = values.value;

    if (isNaN(values.revenue)) newValue.revenue = 0;
    if (isNaN(values.value)) newValue.value = 0;

    const hasValue =
      newValue.value !== undefined &&
      newValue.value >= 0 &&
      newValue.value !== Number(lead.value ?? 0);
    const hasRevenue =
      newValue.revenue !== undefined &&
      newValue.revenue >= 0 &&
      newValue.revenue !== Number(lead.revenue ?? 0);

    const needsLeadUpdate =
      (newValue.full_name !== undefined && newValue.full_name !== lead.name) ||
      (newValue.email !== undefined && newValue.email !== lead.email) ||
      (newValue.company !== undefined && newValue.company !== lead.company) ||
      hasValue ||
      hasRevenue;

    if (needsLeadUpdate) {
      // `updateLead` resolves `false` on failure rather than rejecting, and
      // raises its own toast (Chat.actions.ts) — that is where the BUG-449
      // message is fixed. A `.catch` here would never run, and merging
      // unconditionally painted rejected edits into the panel as if saved.
      const saved = await updateLead(newValue, { leadId: lead.id });
      if (!saved) return false;
      setSelectedLeadForChatModal((prev) => {
        if (!prev || prev.id !== lead.id) return prev;
        const { full_name, ...updatedValues } = newValue;
        return {
          ...prev,
          ...updatedValues,
          name: full_name ?? prev.name,
        };
      });
    }

    return true;
  }

  /**
   * Attach a tag on its own, the mirror image of `deleteTag`: the typeahead
   * pick is the commit, so it must not wait for — or drag along — the other
   * attribute fields the save button owns (BUG-938). Resolves `false` when the
   * tag was not attached, so the caller can undo its own optimistic chip.
   */
  async function attachTag(tag: string): Promise<boolean> {
    const lead = displayedLead ?? selectedLeadForChatModal;
    const tagName = tag.trim();
    if (!lead?.id || !currentWorkspaceId || !currentUserId || !tagName)
      return false;

    const leadId = lead.id;
    const normalizedTagName = tagName.toLowerCase();
    const isTagAlreadyAssigned = (lead.tags || []).some(
      (existingTag) =>
        getTagName(existingTag).trim().toLowerCase() === normalizedTagName,
    );
    if (isTagAlreadyAssigned) return false;

    const tagOperation = { insertedOptimistically: false };

    // Optimistic: paint the chip before the request, as deleteTag does.
    tagOpsInFlight.current++;
    setSelectedLeadForChatModal((prev) => {
      if (!prev || prev.id !== leadId) return prev;
      const existingNames = (prev.tags || []).map((existingTag) =>
        getTagName(existingTag).trim().toLowerCase(),
      );
      if (existingNames.includes(normalizedTagName)) return prev;
      tagOperation.insertedOptimistically = true;
      return {
        ...prev,
        tags: [...(prev.tags || []), tagName],
      };
    });

    try {
      await ChatService.updateTag(
        {
          workspaceId: currentWorkspaceId,
          chatId: leadId,
          attach_tags: [tagName],
        },
        currentUserId,
      );
    } catch (error) {
      // Rollback: remove the tag we optimistically added
      setSelectedLeadForChatModal((prev) => {
        if (!prev || prev.id !== leadId || !tagOperation.insertedOptimistically)
          return prev;
        return {
          ...prev,
          tags: (prev.tags || []).filter(
            (existingTag) =>
              getTagName(existingTag).trim().toLowerCase() !==
              normalizedTagName,
          ),
        };
      });
      addAlert({
        type: eAlertType.ERROR,
        message: `${resolveCopy(translate, 'apiErrors.context.saveTag')} ${resolveApiErrorMessage(error, translate)}`,
      });
      return false;
    } finally {
      tagOpsInFlight.current--;
    }

    onLeadTagsChanged({ leadId, tag: tagName, attached: true });
    // The attach may have created the tag, so the cached workspace catalog the
    // typeahead reads is now behind reality (BUG-940).
    invalidateWorkspaceTags(currentWorkspaceId);
    addAlert({
      type: eAlertType.SUCCESS,
      message: translateChat('attributes.updateSuccess'),
    });
    return true;
  }

  async function deleteTag(tag: string): Promise<void> {
    if (!selectedLeadForChatModal?.id || !currentWorkspaceId || !currentUserId)
      return;

    const leadId = selectedLeadForChatModal.id;

    // Optimistic: remove tag from UI immediately
    tagOpsInFlight.current++;
    setSelectedLeadForChatModal((prev) => {
      if (!prev || prev.id !== leadId) return prev;
      return {
        ...prev,
        tags: (prev.tags || []).filter((t) => getTagName(t) !== tag),
      };
    });

    return ChatService.deleteTag(
      {
        workspaceId: currentWorkspaceId,
        chatId: leadId,
        detach_tags: [tag],
      },
      currentUserId,
    )
      .then(() => {
        onLeadTagsChanged({ leadId, tag, attached: false });
        // Re-read the server catalog after detaching instead of assuming
        // whether the tag remains available in the workspace (BUG-940).
        invalidateWorkspaceTags(currentWorkspaceId);
      })
      .catch((error) => {
        // Rollback: restore the tag we optimistically removed
        setSelectedLeadForChatModal((prev) => {
          if (!prev || prev.id !== leadId) return prev;
          const existingNames = (prev.tags || []).map(getTagName);
          if (existingNames.includes(tag)) return prev;
          return {
            ...prev,
            tags: [...(prev.tags || []), tag],
          };
        });
        addAlert({
          type: eAlertType.ERROR,
          message: `${resolveCopy(translate, 'apiErrors.context.deleteTag')} ${resolveApiErrorMessage(error, translate)}`,
        });
      })
      .finally(() => {
        tagOpsInFlight.current--;
      });
  }

  return {
    onChatInfoAttributesSubmit,
    attachTag,
    deleteTag,
    tagOpsInFlight,
  };
}
